import React, { createContext, useContext, useEffect } from 'react';
import { Link as LinkIcon } from '@mui/icons-material';
import { Box, Link as MuiLink, List as MuiList, ListItemText, ListProps, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { APP_HEADER_HEIGHT_SM, APP_HEADER_HEIGHT_XS, TABS_BAR_HEIGHT } from '../../utils';

// Derive a stable URL slug from heading text, so every section is deep-linkable without call sites
// passing an id. Deterministic: the slug IS the shareable anchor, so renaming a heading changes its
// link. e.g. "Season-Long Payout" -> "season-long-payout".
function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

type Section = { title: string; summary: string };

// The single source of truth for the main sections, in render order. Both the Table of Contents and
// each section's heading + "in short" summary read from here, so a section's title and description can
// never drift between the two. A section added below is added here once to become fully navigable.
//
// The list is built per-render from `hasBuyIn`: a league with no buy-in has no prize pool, so the two
// payout sections — and the buy-in mention in the General Rules summary — don't apply and are dropped.
// It's threaded to RuleSection and TableOfContents through SectionsContext so those deeply-nested
// components stay in lockstep with whatever the top-level component built.
function buildSections(hasBuyIn: boolean): Section[] {
  return [
    {
      title: 'General Rules',
      summary: hasBuyIn
        ? 'The basics — buy-in, how games are picked, and how a pick is scored.'
        : 'The basics — how games are picked, and how a pick is scored.',
    },
    {
      title: 'The Season',
      summary: 'How regular season, conference championship, and playoff weeks are scored.',
    },
    ...(hasBuyIn
      ? [
          {
            title: 'Weekly Payouts',
            summary: "Each week's pot goes to whoever gets the most correct picks that week.",
          },
          {
            title: 'Season-Long Payout',
            summary: "What's left of the buy-in pool after weekly payouts goes to the season's overall leader.",
          },
        ]
      : []),
  ];
}

const SectionsContext = createContext<Section[]>(buildSections(true));

function BulletList({ children, sx, ...rest }: ListProps) {
  return (
    <MuiList
      dense
      disablePadding
      sx={{
        listStyle: 'disc',
        pl: 4,
        mt: 0.5,
        '& > .MuiListItemText-root': {
          display: 'list-item',
          mt: 0.75,
          pr: 0,
        },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </MuiList>
  );
}

// Whether the page this is rendered into has a sticky league tab bar (see useTabs's `sticky` option)
// stacked below the app bar. False by default (matching the public rules page, which has no such tab
// bar); the in-league tab overrides this to true, since it renders below the league's sticky tabs. Read
// by LinkableHeading, which is nested many levels below the top-level component's `stickyTabBar` prop --
// a context avoids threading that prop through every RuleSection/SubHeading call site.
const StickyTabBarContext = createContext(false);

// A heading you can link straight to. The heading is the anchor target (id = slug); hovering reveals a
// # icon whose href is `#slug`, so a click drops the anchor into the address bar to copy and share.
// scrollMarginTop keeps the target clear of the sticky app bar and, where present, the sticky league
// tab bar rendered above this page on deep-link scroll.
function LinkableHeading({
  id,
  variant,
  sx,
  children,
}: {
  id: string;
  variant: 'h6' | 'subtitle1';
  sx?: object;
  children: React.ReactNode;
}) {
  const stickyTabBar = useContext(StickyTabBarContext);
  const tabsBarOffset = stickyTabBar ? TABS_BAR_HEIGHT : 0;
  return (
    <Typography
      id={id}
      variant={variant}
      sx={{
        scrollMarginTop: {
          xs: APP_HEADER_HEIGHT_XS + tabsBarOffset + 16,
          sm: APP_HEADER_HEIGHT_SM + tabsBarOffset + 16,
        },
        fontWeight: variant === 'subtitle1' ? 'bold' : undefined,
        '& .anchor-link': { opacity: 0, transition: 'opacity 0.15s' },
        '&:hover .anchor-link, & .anchor-link:focus-visible': { opacity: 1 },
        ...sx,
      }}
    >
      {children}
      <MuiLink
        href={`#${id}`}
        className="anchor-link"
        aria-label="Link to this section"
        sx={{ ml: 1, verticalAlign: 'middle', color: 'text.secondary', lineHeight: 0 }}
      >
        <LinkIcon fontSize="small" />
      </MuiLink>
    </Typography>
  );
}

// The heading style used atop each of the page's boxed orientation elements (Overview, Contents), so
// they read as one family of "read this before the rules" callouts rather than differently-styled intro
// content.
function BoxLabel({ children }: { children: React.ReactNode }) {
  return <Typography variant="h6">{children}</Typography>;
}

// One plain-language sentence per section: read this, or read the fine print below it.
function SectionSummary({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
      {children}
    </Typography>
  );
}

// A tinted, accented box for a worked example, so it reads as "here's a concrete case" instead of more
// prose — which is exactly what people skim these rules for.
function ExampleCallout({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        borderLeft: 3,
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
        borderRadius: 1,
      }}
    >
      <Typography variant="overline" color="primary" sx={{ display: 'block', lineHeight: 1.5 }}>
        Example
      </Typography>
      {children}
    </Box>
  );
}

// One section. Its title-derived slug is the anchor target, and its number + "in short" summary are
// sourced from SECTIONS by position, so the number and description always match the Table of Contents.
// The number is display-only — the slug stays the plain title, so anchor links survive a reordering.
function RuleSection({ title, children }: { title: string; children: React.ReactNode }) {
  const sections = useContext(SectionsContext);
  const id = slugify(title);
  const index = sections.findIndex(section => section.title === title);
  const summary = index >= 0 ? sections[index].summary : undefined;
  return (
    <Box component="section" sx={{ mt: 3 }}>
      <LinkableHeading id={id} variant="h6">
        {index + 1}. {title}
      </LinkableHeading>
      {summary && <SectionSummary>{summary}</SectionSummary>}
      {children}
    </Box>
  );
}

// Boxed and labeled the same way as Contents below it, so its purpose — the fast version, full detail
// further down — reads as deliberate rather than as throwaway intro prose.
function Tldr({ hasBuyIn }: { hasBuyIn: boolean }) {
  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
      <BoxLabel>Overview</BoxLabel>
      <Typography variant="body1" sx={{ mt: 1 }}>
        Every week, the commissioner picks 10 games. Your job is simple: pick the winner of each one against the spread.
        Here&apos;s the shape of a season:
      </Typography>
      <BulletList>
        {hasBuyIn && (
          <ListItemText>
            <strong>Buy in.</strong> Everyone pays a $20 entry fee into a shared prize pool.
          </ListItemText>
        )}
        <ListItemText>
          <strong>Pick against the spread.</strong> Each week, pick a side for 10 games chosen by the commissioner.
          {hasBuyIn
            ? " Whoever gets the most right that week wins that week's pot."
            : ' Whoever gets the most right that week tops the standings.'}
        </ListItemText>
        <ListItemText>
          <strong>Play the whole season.</strong> Regular season, conference championship weekend, and the playoff each
          have their own scored week — 15 in total.
        </ListItemText>
        {hasBuyIn && (
          <ListItemText>
            <strong>Chase the season lead.</strong> After every weekly pot is paid out, what&apos;s left of the buy-in
            pool goes to whoever had the most correct picks across the entire season.
          </ListItemText>
        )}
      </BulletList>
    </Box>
  );
}

// Doubles as an index and a finder: each entry is a jump link plus the section's description, so a
// reader can scan the summaries to figure out which section answers their question before diving in.
function TableOfContents() {
  const sections = useContext(SectionsContext);
  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
      <BoxLabel>Contents</BoxLabel>
      <Box component="ul" sx={{ columns: { xs: 1, md: 2 }, listStyle: 'none', p: 0, m: 0, mt: 1 }}>
        {sections.map(({ title, summary }, index) => (
          <Box component="li" key={title} sx={{ breakInside: 'avoid', mb: 1.5 }}>
            <MuiLink href={`#${slugify(title)}`} underline="hover" sx={{ fontWeight: 500 }}>
              {index + 1}. {title}
            </MuiLink>
            <Typography variant="body2" color="text.secondary">
              {summary}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// title defaults to "League Rules" for the in-league tab, where the Pick 'Em context is already
// established by the league you're sitting in. Callers outside that context (the public rules page)
// pass the fuller "Pick 'Em League Rules" since nothing else on the page says what kind of league.
export default function PickEmRules({
  title = 'League Rules',
  headerAction,
  stickyTabBar = false,
  hasBuyIn = true,
}: {
  title?: string;
  // Rendered next to the title in a flex row (e.g. the public page's "Back to All Rules" link) --
  // nothing here needs it, since the in-league tab has the league's own navigation.
  headerAction?: React.ReactNode;
  // The in-league tab renders through useTabs, which passes { leagueId } to every tab's Component --
  // unused here, but must be accepted so this stays assignable alongside the other tabs.
  leagueId?: string;
  // See StickyTabBarContext above. Defaults to false (matching the public rules page, which has no
  // sticky tab bar to clear); the in-league tab overrides this to true via a wrapper component.
  stickyTabBar?: boolean;
  // Whether this league collects a buy-in. Defaults to true (the public rules page describes the
  // standard $20 buy-in league). A league configured with no buy-in has no prize pool, so the buy-in
  // and payout rules don't apply and are omitted entirely -- see buildSections above.
  hasBuyIn?: boolean;
}) {
  const sections = buildSections(hasBuyIn);
  // Deep links (/league/:id/rules#weekly-payouts) can't rely on the browser's native anchor scroll: when
  // the URL is first read the tab's content hasn't mounted, so the target id doesn't exist yet. Scroll
  // to it ourselves once rendered, and again whenever the hash changes (Contents / anchor click).
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <StickyTabBarContext.Provider value={stickyTabBar}>
      <SectionsContext.Provider value={sections}>
        <Box sx={{ maxWidth: 1000, width: '100%', mt: 2, mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">{title}</Typography>
            {headerAction}
          </Box>

          <Tldr hasBuyIn={hasBuyIn} />

          {/* With no buy-in only two sections remain (General Rules, The Season) -- a table of contents
              for two entries is noise, so it's shown only for the full buy-in ruleset. */}
          {hasBuyIn && <TableOfContents />}

          <RuleSection title="General Rules">
            <BulletList>
              {hasBuyIn && <ListItemText>Each player buys in for $20 at the start of the season.</ListItemText>}
              <ListItemText>
                Each week, the commissioner selects 10 games from that week&apos;s schedule to be available for picks.
              </ListItemText>
              <ListItemText>
                For each game, pick the side you think will win <strong>against the spread</strong> — not just who wins
                the game outright.
              </ListItemText>
            </BulletList>
            <ExampleCallout>
              <Typography variant="body2">
                Team A is favored by 7.5 points over Team B. Picking Team A only counts as correct if Team A wins by 8
                or more; if Team A wins by 7 or fewer (or loses outright), the correct pick was Team B.
              </Typography>
            </ExampleCallout>
            <BulletList>
              <ListItemText>
                If you don&apos;t submit a pick for a game, it counts the same as picking wrong — every game the
                commissioner selects counts against you whether or not you picked it.
              </ListItemText>
              <ListItemText>
                The commissioner has final say on which games are selected each week, the spread used for each game, and
                on interpreting these rules.
              </ListItemText>
            </BulletList>
          </RuleSection>

          <RuleSection title="The Season">
            <BulletList>
              <ListItemText>The regular season is 13 scored weeks.</ListItemText>
              <ListItemText>Conference championship weekend is its own scored week.</ListItemText>
              <ListItemText>
                The playoff is one final scored week. As the bracket plays out over about a month and later rounds&apos;
                matchups aren&apos;t known until earlier ones finish, games get added to this same week as they&apos;re
                set
                {hasBuyIn
                  ? " — the payout for it doesn't go out until the national championship game is over."
                  : " — it isn't finalized until the national championship game is over."}
              </ListItemText>
              <ListItemText>That&apos;s 15 scored weeks across the season.</ListItemText>
              {!hasBuyIn && (
                <ListItemText>
                  Compete each week, and for the highest total throughout the season. Have fun!
                </ListItemText>
              )}
            </BulletList>
          </RuleSection>

          {hasBuyIn && (
            <>
              <RuleSection title="Weekly Payouts">
                <BulletList>
                  <ListItemText>
                    5% of each player&apos;s buy-in — $1 per player — funds that week&apos;s pot.
                  </ListItemText>
                  <ListItemText>
                    Whoever has the most correct picks against the spread that week, out of the 10 selected games, wins
                    the pot for that week.
                  </ListItemText>
                  <ListItemText>
                    If multiple players tie for the most correct picks, the pot is split evenly among them.
                  </ListItemText>
                </BulletList>
                <ExampleCallout>
                  <Typography variant="body2">
                    A 12-player league&apos;s weekly pot is $12. Two players tie for the most correct picks that week,
                    so each receives $6.
                  </Typography>
                </ExampleCallout>
              </RuleSection>

              <RuleSection title="Season-Long Payout">
                <BulletList>
                  <ListItemText>
                    After all 15 weekly pots are paid out ($15 of each player&apos;s $20 buy-in), the remaining $5 per
                    player — 25% of the buy-in — funds one final pot.
                  </ListItemText>
                  <ListItemText>
                    It&apos;s paid to whoever has the most total correct picks across the entire season — every regular
                    season week, the conference championship week, and the playoff week, all counted equally.
                  </ListItemText>
                  <ListItemText>Ties split the pot evenly, the same as a weekly payout.</ListItemText>
                </BulletList>
                <ExampleCallout>
                  <Typography variant="body2">
                    A 12-player league&apos;s season-long pot is $60. Two players tie for the most correct picks across
                    the whole season, so each receives $30.
                  </Typography>
                </ExampleCallout>
              </RuleSection>
            </>
          )}
        </Box>
      </SectionsContext.Provider>
    </StickyTabBarContext.Provider>
  );
}
